import { useEffect, useRef, useState } from 'react';
import { Pressable, useWindowDimensions } from 'react-native';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import Svg, { Circle, Path } from 'react-native-svg';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import GuideCard from './GuideCard';
import { guideArrowPath, placeGuideCard } from '@/utils/guides';

const MOTION = { duration: 280, easing: Easing.inOut(Easing.cubic) };
const PAD = 6;

/**
 * Shared interior for both guide overlays: spotlight scrim built from four
 * animated views (rounded ring, dotted connector, tap-to-activate hotspot)
 * and the positioning wrapper for the guide card. Platform files only supply
 * the container (portal/modal). Scrim uses views instead of animated SVG
 * paths because RNSVG animated props and entering animations are unreliable
 * inside a native Modal on the New Architecture.
 */
export default function GuideStage({ target, step, measuring, animate = true, insetTop = 0, insetBottom = 0, screenReader, viewport, onActivate, ...cardProps }) {
    const local = useWindowDimensions();
    const { width, height } = viewport || local;
    const { theme: { scrimColor, scheme, colors } } = useUnistyles();
    const scrim = scheme === 'dark' ? 'rgba(0, 0, 0, 0.82)' : scrimColor;
    const arrowColor = colors.primary;
    const [cardHeight, setCardHeight] = useState(280);
    const [slowMeasure, setSlowMeasure] = useState(false);
    const hole = useSharedValue(null);
    const cardPos = useSharedValue(null);
    const fade = useSharedValue(0);
    const arrowFade = useSharedValue(0);
    const lastTarget = useRef(null);
    const visibleTarget = screenReader ? null : target;
    // Keep the last good rect while the next step re-measures so neither the
    // hole nor the card flickers back to a fallback position mid-transition.
    if (visibleTarget) lastTarget.current = visibleTarget;
    const shownTarget = visibleTarget || lastTarget.current;

    useEffect(() => {
        fade.value = withTiming(1, { duration: 240 });
    }, []);

    // Only settle into the "Preparing" copy when measuring takes a while —
    // a fast remeasure between steps shouldn't flash placeholder text.
    useEffect(() => {
        if (!measuring) { setSlowMeasure(false); return; }
        const timer = setTimeout(() => setSlowMeasure(true), 600);
        return () => clearTimeout(timer);
    }, [measuring, step?.featureID]);

    // Seed shared values synchronously so the first painted frame already has
    // the cutout and the card in place instead of landing at 0,0.
    if (shownTarget && hole.value === null) {
        hole.value = { x: shownTarget.x, y: shownTarget.y, width: shownTarget.width, height: shownTarget.height };
    }
    const placement = placeGuideCard({ target: shownTarget, width, height, cardHeight, insetTop, insetBottom });
    if (cardPos.value === null) cardPos.value = { x: placement.left, y: placement.top };

    useEffect(() => {
        if (!shownTarget) return;
        const next = { x: shownTarget.x, y: shownTarget.y, width: shownTarget.width, height: shownTarget.height };
        hole.value = !animate ? next : withTiming(next, MOTION);
        arrowFade.value = 0;
        arrowFade.value = withTiming(1, { duration: 320, easing: Easing.out(Easing.cubic) });
    }, [shownTarget?.x, shownTarget?.y, shownTarget?.width, shownTarget?.height, animate]);

    useEffect(() => {
        const next = { x: placement.left, y: placement.top };
        cardPos.value = !animate ? next : withTiming(next, MOTION);
    }, [placement.left, placement.top, animate]);

    const fadeStyle = useAnimatedStyle(() => ({ opacity: fade.value }));
    const topStyle = useAnimatedStyle(() => ({ height: hole.value ? Math.max(0, hole.value.y - PAD) : height }));
    const bottomStyle = useAnimatedStyle(() => {
        const bottom = hole.value ? Math.min(height, hole.value.y + hole.value.height + PAD) : height;
        return { top: bottom, height: Math.max(0, height - bottom) };
    });
    const leftStyle = useAnimatedStyle(() => {
        if (!hole.value) return { top: 0, height: 0, width: 0 };
        const top = Math.max(0, hole.value.y - PAD);
        const bottom = Math.min(height, hole.value.y + hole.value.height + PAD);
        return { top, height: Math.max(0, bottom - top), width: Math.max(0, hole.value.x - PAD) };
    });
    const rightStyle = useAnimatedStyle(() => {
        if (!hole.value) return { top: 0, left: 0, height: 0, width: 0 };
        const top = Math.max(0, hole.value.y - PAD);
        const bottom = Math.min(height, hole.value.y + hole.value.height + PAD);
        const left = Math.min(width, hole.value.x + hole.value.width + PAD);
        return { top, left, height: Math.max(0, bottom - top), width: Math.max(0, width - left) };
    });
    const ringStyle = useAnimatedStyle(() => hole.value
        ? { left: hole.value.x - PAD, top: hole.value.y - PAD, width: hole.value.width + PAD * 2, height: hole.value.height + PAD * 2, opacity: fade.value }
        : { opacity: 0 });
    const arrowStyle = useAnimatedStyle(() => ({ opacity: Math.min(fade.value, arrowFade.value) }));
    const cardAnimStyle = useAnimatedStyle(() => ({
        opacity: fade.value,
        transform: cardPos.value ? [{ translateX: cardPos.value.x }, { translateY: cardPos.value.y }] : [],
    }));

    const arrow = shownTarget ? guideArrowPath(placement.left, placement.top, placement.width, cardHeight, shownTarget) : { path: '', dot: null };

    return (
        <>
            <Animated.View pointerEvents="none" testID="guide-scrim" style={[StyleSheet.absoluteFill, fadeStyle]}>
                <Animated.View style={[styles.pane, { top: 0, left: 0, width, backgroundColor: scrim }, topStyle]} />
                <Animated.View style={[styles.pane, { left: 0, width, backgroundColor: scrim }, bottomStyle]} />
                <Animated.View style={[styles.pane, { left: 0, backgroundColor: scrim }, leftStyle]} />
                <Animated.View style={[styles.pane, { backgroundColor: scrim }, rightStyle]} />
            </Animated.View>
            <Animated.View pointerEvents="none" testID="guide-ring" style={[styles.ring(arrowColor), ringStyle]} />
            <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, arrowStyle]}>
                <Svg width={width} height={height}>
                    {!!arrow.path && <Path testID="guide-arrow" d={arrow.path} stroke={arrowColor} strokeWidth={4} strokeLinecap="round" strokeDasharray="0.5 12" fill="none" />}
                    {!!arrow.dot && <Circle testID="guide-arrow-dot" cx={arrow.dot.x} cy={arrow.dot.y} r={5} fill={arrowColor} />}
                </Svg>
            </Animated.View>
            {!!onActivate && !!shownTarget && (
                <Pressable
                    testID="guide-activate"
                    accessibilityRole="button"
                    accessibilityLabel={`Try: ${step?.title || 'highlighted control'}`}
                    onPress={onActivate}
                    style={styles.hotspot(shownTarget)}
                />
            )}
            <Animated.View style={[styles.cardWrap(placement.width), cardAnimStyle]}>
                <GuideCard {...cardProps} animate={animate} step={step} interactive={!!onActivate && !screenReader} measuring={measuring && slowMeasure} style={{ maxHeight: height - insetTop - insetBottom - 32 }} onLayout={event => setCardHeight(event.nativeEvent.layout.height)} />
            </Animated.View>
        </>
    );
}

const styles = StyleSheet.create(() => ({
    pane: { position: 'absolute', top: 0 },
    ring: color => ({ position: 'absolute', borderRadius: 14, borderWidth: 1.5, borderColor: color }),
    hotspot: target => ({
        position: 'absolute',
        left: Math.max(0, target.x - 8),
        top: Math.max(0, target.y - 8),
        width: target.width + 16,
        height: target.height + 16,
        borderRadius: 14,
        _web: { cursor: 'pointer' },
    }),
    cardWrap: cardWidth => ({ position: 'absolute', left: 0, top: 0, width: cardWidth }),
}));
